/**
 * AIMDv2 – useAIMDWizard
 *
 * Central state-management hook for the entire wizard.
 *
 * Responsibilities:
 * 1. Initialises state from localStorage (or falls back to INITIAL_WIZARD_STATE).
 * 2. Persists every state mutation to localStorage automatically.
 * 3. Exposes granular updaters so step components never import the full state.
 * 4. Runs per-step validation before allowing navigation forward.
 * 5. Calls the real backend to save buildState and create program-generation records.
 * 6. Reads the current groupId from Redux (selectedGroup).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AIMDv2WizardState,
  RequirementsData,
  OutcomesData,
  DocumentsData,
  PreviewData,
  StructureData,
  ContentData,
  GenerationStatus,
  MembersData,
  MemberAttributeGroup,
  StepValidationResult,
  AIMD_STEPS
} from '../types';
import { INITIAL_WIZARD_STATE, LOCAL_STORAGE_KEY, STEP_META, canNavigateToStep } from '../constants';
import { validateStep } from './useStepValidation';
import { saveBuildState, createProgramGeneration, saveAudienceSelection } from '../api';
import useProgramCreationFlowV2 from '../../../../../hooks/useProgramCreationFlowV2';
import { isBuildStateWritable, useBuilderWritable } from '../../../../../components/program-collaboration';

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

function loadFromStorage(): AIMDv2WizardState | null {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AIMDv2WizardState;
  } catch {
    return null;
  }
}

function saveToStorage(state: AIMDv2WizardState): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    console.error('[AIMDv2] Failed to save to localStorage', err);
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface AIMDWizardReturn {
  state: AIMDv2WizardState;
  currentStepId: (typeof AIMD_STEPS)[number];
  currentStepMeta: (typeof STEP_META)[number];
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  isSaving: boolean;
  validation: StepValidationResult;
  /** The active groupId from Redux. */
  groupId: string;

  goNext: () => boolean;
  goPrev: () => void;
  goToStep: (index: number) => void;

  /** Save the full wizard state (incl. edited lesson content) to the BE buildState. */
  persistBuildState: () => Promise<void>;

  updateRequirements: (patch: Partial<RequirementsData>) => void;
  updateOutcomes: (patch: Partial<OutcomesData>) => void;
  updateDocuments: (patch: Partial<DocumentsData>) => void;
  updatePreview: (patch: Partial<PreviewData>) => void;
  updateStructure: (patch: Partial<StructureData>) => void;
  updateContent: (patchOrUpdater: Partial<ContentData> | ((prev: ContentData) => ContentData)) => void;
  updateMembers: (patch: Partial<MembersData>) => void;

  /** Cache the org attribute groups so the PUT payload can be built on save. */
  setOrgAttributeGroups: (groups: MemberAttributeGroup[]) => void;

  /** Requirements reports whether the org has any attribute list to pick an audience from. */
  setAudienceAvailable: (available: boolean) => void;

  /** Set the generation ID directly. */
  setGenerationId: (genId: string) => void;

  /** Set the groupId (from the backend-created group). */
  setGroupId: (id: string) => void;

  /** Persist current state to backend (buildState + generation record). */
  saveToBackend: () => Promise<void>;
  resetWizard: () => void;
}

export function useAIMDWizard(): AIMDWizardReturn {
  // groupId is now set exclusively by the backend via createProgramGeneration
  // (which auto-creates a fresh group). No longer read from Redux.

  const [state, setState] = useState<AIMDv2WizardState>(() => {
    const stored = loadFromStorage();
    return stored ?? { ...INITIAL_WIZARD_STATE };
  });
  const [isSaving, setIsSaving] = useState(false);

  const groupId = state.groupId || '';
  // Creation-flow revamp gate — controls the deferred-enrollment audience save.
  const creationFlowV2 = useProgramCreationFlowV2();
  // Co-design: only the admin holding the edit lock may write this program's
  // buildState. While the lock is being acquired, or another admin holds it,
  // this copy may be a stale snapshot and must never be written back over
  // their work.
  const writable = useBuilderWritable();
  const stateRef = useRef(state);
  stateRef.current = state;

  /**
   * Stores the full org attribute catalog fetched by RequirementsStep.
   * Needed to build the PUT payload when saving audience selection.
   */
  const orgAttributeGroupsRef = useRef<MemberAttributeGroup[]>([]);
  /** Whether the org has an attribute list to pick an audience from (null until Requirements knows). */
  const [audienceAvailable, setAudienceAvailable] = useState<boolean | null>(null);

  // Persist to localStorage on every state change
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    saveToStorage({ ...state, lastSavedAt: new Date().toISOString() });
  }, [state]);

  // Persist build MILESTONES to the BE (debounced) so a resume on ANOTHER
  // device reopens on the right step WITH the generated artifacts. `goNext`
  // alone lags: it saves the PRE-advance snapshot and never captures the
  // structure/content generated while sitting on a step (the cross-device
  // "back to Documents instead of Structure" bug). Keyed on the artifacts +
  // generationId + completion history — not every keystroke.
  const beMilestoneInit = useRef(true);
  useEffect(() => {
    if (beMilestoneInit.current) {
      // Skip the first run — this is the freshly-loaded/hydrated state; saving
      // it straight back would be redundant (and could race the resume seed).
      beMilestoneInit.current = false;
      return undefined;
    }
    if (!groupId || !writable) return undefined;
    // Short debounce: these milestones change only on navigation / generation
    // completion (not per-keystroke and NOT during content polling, which isn't
    // a dep), so persist them near-immediately — this is what makes a resume on
    // another device land on the step the admin is actually on, with no race.
    const handler = setTimeout(() => {
      // Re-check at fire time: a reload from the BE may have started meanwhile.
      if (!isBuildStateWritable(groupId)) return;
      saveBuildState(groupId, state).catch((err) =>
        console.error('[AIMDv2] milestone buildState save failed', err)
      );
    }, 300);
    return () => clearTimeout(handler);
    // `writable` is a dep so a save skipped while acquiring the lock runs once
    // the lock is held.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    groupId,
    writable,
    state.generationId,
    state.preview.generatedPreview,
    state.structure.generatedStructure,
    state.highestCompletedStep,
    state.currentStep
  ]);

  // Persist the FULL wizard state (including edited lesson content, which the
  // milestone save above does not track) so collaborating admins — and a
  // resume on another device — see the saved edits, not just the structure.
  const persistBuildState = useCallback(async () => {
    if (!groupId || !isBuildStateWritable(groupId)) return;
    await saveBuildState(groupId, stateRef.current);
  }, [groupId]);

  // ------ Derived values ------

  const currentStepId = AIMD_STEPS[state.currentStep];
  const currentStepMeta = STEP_META[state.currentStep];
  const totalSteps = AIMD_STEPS.length;
  const isFirstStep = state.currentStep === 0;
  const isLastStep = state.currentStep === totalSteps - 1;

  const validation = useMemo(
    () => validateStep(state, state.currentStep, { audienceAvailable }),
    [state, audienceAvailable]
  );

  // ------ Navigation ------

  const goNext = useCallback((): boolean => {
    const result = validateStep(state, state.currentStep, { audienceAvailable });
    if (!result.isValid) return false;

    const leavingStep = AIMD_STEPS[state.currentStep];

    setState((prev) => {
      const nextStep = Math.min(prev.currentStep + 1, totalSteps - 1);
      return {
        ...prev,
        currentStep: nextStep,
        highestCompletedStep: Math.max(prev.highestCompletedStep, prev.currentStep)
      };
    });

    // Fire-and-forget backend saves on step advance
    if (groupId) {
      saveBuildState(groupId, state).catch((err) =>
        console.error('[AIMDv2] buildState save failed', err)
      );

      // Save audience selection to backend when leaving Requirements step.
      // Creation-flow revamp: enroll=false — the wizard's audience save was
      // silently enrolling matching members long before the Members step;
      // enrollment now only fires on "Invite & Finish".
      if (leavingStep === 'requirements' && orgAttributeGroupsRef.current.length > 0) {
        saveAudienceSelection(
          groupId,
          state.requirements.audience,
          orgAttributeGroupsRef.current,
          creationFlowV2 ? { enroll: false } : undefined
        ).catch((err) =>
          console.error('[AIMDv2] audience save failed', err)
        );
      }

      // generationId creation is handled by the AIMDContext's ensureGenerationId
      // to avoid duplicate calls when PreviewStep auto-triggers on mount.
    }

    return true;
  }, [state, totalSteps, groupId, creationFlowV2, audienceAvailable]);

  const goPrev = useCallback(() => {
    setState((prev) => {
      const target = Math.max(prev.currentStep - 1, 0);
      return canNavigateToStep(prev.currentStep, target) ? { ...prev, currentStep: target } : prev;
    });
  }, []);

  const goToStep = useCallback(
    (index: number) => {
      if (index < 0 || index >= totalSteps) return;
      if (index > state.highestCompletedStep + 1) return;
      setState((prev) => (canNavigateToStep(prev.currentStep, index) ? { ...prev, currentStep: index } : prev));
    },
    [totalSteps, state.highestCompletedStep]
  );

  // ------ Data updaters ------

  const updateRequirements = useCallback((patch: Partial<RequirementsData>) => {
    setState((prev) => ({
      ...prev,
      requirements: { ...prev.requirements, ...patch }
    }));
  }, []);

  const updateOutcomes = useCallback((patch: Partial<OutcomesData>) => {
    setState((prev) => ({
      ...prev,
      outcomes: { ...prev.outcomes, ...patch }
    }));
  }, []);

  const updateDocuments = useCallback((patch: Partial<DocumentsData>) => {
    setState((prev) => ({
      ...prev,
      documents: { ...prev.documents, ...patch }
    }));
  }, []);

  const updatePreview = useCallback((patch: Partial<PreviewData>) => {
    setState((prev) => ({
      ...prev,
      preview: { ...prev.preview, ...patch }
    }));
  }, []);

  const updateStructure = useCallback((patch: Partial<StructureData>) => {
    setState((prev) => ({
      ...prev,
      structure: { ...prev.structure, ...patch }
    }));
  }, []);

  // Use a ref so the stable useCallback wrapper always executes the latest
  // implementation — critical because useCallback([]) survives HMR unchanged.
  const updateContentImplRef = useRef<
    (arg: Partial<ContentData> | ((prev: ContentData) => ContentData)) => void
  >(() => {});

  updateContentImplRef.current = (patchOrUpdater) => {
    setState((prev) => {
      // Functional updater: caller has full control (used by handleEditSave, etc.)
      if (typeof patchOrUpdater === 'function') {
        return { ...prev, content: patchOrUpdater(prev.content) };
      }

      // Patch-based update (used by polling): auto-merge to preserve
      // locally-completed lessons so edits are never overwritten.
      const merged = { ...prev.content, ...patchOrUpdater };

      const incoming = patchOrUpdater.generatedContent as GenerationStatus | undefined;
      const existing = prev.content.generatedContent as GenerationStatus | undefined;

      if (incoming?.modules && existing?.modules) {
        const mergedModules = incoming.modules.map((srvMod, mIdx) => {
          const locMod = existing.modules[mIdx];
          if (!locMod) return srvMod;

          const mergedLessons = srvMod.lessons.map((srvLesson, lIdx) => {
            const locLesson = locMod.lessons?.[lIdx];
            if (locLesson && locLesson.status === 'completed') return locLesson;
            return srvLesson;
          });

          return {
            ...srvMod,
            lessons: mergedLessons,
            completedLessonsCount: mergedLessons.filter((l) => l.status === 'completed').length
          };
        });

        merged.generatedContent = { ...incoming, modules: mergedModules };
      }

      return { ...prev, content: merged };
    });
  };

  const updateContent = useCallback(
    (patchOrUpdater: Partial<ContentData> | ((prev: ContentData) => ContentData)) => {
      updateContentImplRef.current(patchOrUpdater);
    },
    []
  );

  const updateMembers = useCallback((patch: Partial<MembersData>) => {
    setState((prev) => ({
      ...prev,
      members: { ...prev.members, ...patch }
    }));
  }, []);

  const setOrgAttributeGroups = useCallback((groups: MemberAttributeGroup[]) => {
    orgAttributeGroupsRef.current = groups;
  }, []);

  const setGenerationId = useCallback((genId: string) => {
    setState((prev) => ({ ...prev, generationId: genId }));
  }, []);

  const setGroupId = useCallback((id: string) => {
    setState((prev) => ({ ...prev, groupId: id }));
  }, []);

  // ------ Backend persistence ------

  const saveToBackend = useCallback(async () => {
    setIsSaving(true);
    try {
      let currentGroupId = state.groupId;
      if (!state.generationId) {
        const result = await createProgramGeneration();
        setState((prev) => ({
          ...prev,
          generationId: result.generationId,
          groupId: result.groupId
        }));
        currentGroupId = result.groupId;
      }
      if (currentGroupId) {
        await saveBuildState(currentGroupId, state);
      }
    } catch (err) {
      console.error('[AIMDv2] Backend save failed', err);
    } finally {
      setIsSaving(false);
    }
  }, [state]);

  const resetWizard = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    setState({ ...INITIAL_WIZARD_STATE });
  }, []);

  return {
    state,
    currentStepId,
    currentStepMeta,
    totalSteps,
    isFirstStep,
    persistBuildState,
    isLastStep,
    isSaving,
    validation,
    groupId,
    goNext,
    goPrev,
    goToStep,
    updateRequirements,
    updateOutcomes,
    updateDocuments,
    updatePreview,
    updateStructure,
    updateContent,
    updateMembers,
    setOrgAttributeGroups,
    setAudienceAvailable,
    setGenerationId,
    setGroupId,
    saveToBackend,
    resetWizard
  };
}
